<script setup lang="ts">
import { watchDebounced } from "@vueuse/core";
import type { EventDetails, ProductDetails } from "mws-ts-v2-sdk";

type Props = {
  product: ProductDetails;
  event: EventDetails;
  bidAmount: string;
};

const { bidAmount, event, product } = defineProps<Props>();
const costBreakDownOpen = ref(false);

const { isShippingLocationConfirmed, setShippingLocation } =
  useShippingLocation();

const confirmDetectedShippingLocation = () => {
  const estimatedUserCountry = getEstimatedUserCountry();

  if (estimatedUserCountry) setShippingLocation(estimatedUserCountry);
};

const debouncedBidAmount = ref(0);

const isEnabled = computed(
  () => !isNaN(Number(bidAmount)) && isShippingLocationConfirmed.value,
);

const {
  data: bidCosts,
  status: bidCostsStatus,
  providedFilters,
} = useBidCosts({
  product: () => product,
  bidAmount: () => debouncedBidAmount.value,
  enabled: isEnabled,
});

const { eventName } = useEventDetails(() => event);

const isSalesTax = useIsSalesTax(bidCosts);

const totalCostsInfo = computed(() => {
  if (!bidCosts.value) return;

  const vatPrice = bidCosts.value.vat?.price || 0;
  const buyersPremiumPrice = bidCosts.value.buyersPremium?.price || 0;

  if (buyersPremiumPrice > 0 && vatPrice > 0) {
    if (isSalesTax.value) return "product.costs.totalIncludesSalesTaxAll";

    return "product.costs.totalIncludesAll";
  }

  if (buyersPremiumPrice === 0 && vatPrice > 0) {
    if (isSalesTax.value) return "product.costs.totalIncludesSalesTaxShipping";

    return "product.costs.totalIncludesVatShipping";
  }

  if (buyersPremiumPrice > 0 && vatPrice === 0) {
    return "product.costs.totalIncludesBuyersPremiumShipping";
  }

  return "product.costs.totalIncludesShipping";
});

watchDebounced(
  () => bidAmount,
  () => {
    if (!isNaN(Number(bidAmount))) {
      debouncedBidAmount.value = Number(bidAmount);
    } else {
      throw new TypeError("Expected a Number but got a string as a bid amount");
    }
  },
  {
    debounce: 500,
    immediate: true,
  },
);
</script>

<template>
  <ClientOnly>
    <BaseTransitionFadeBetween>
      <button
        v-if="!isShippingLocationConfirmed"
        class="flex justify-center uppercase text-sm text-platform-text-1 w-full h-11 items-center"
        @click="confirmDetectedShippingLocation"
      >
        {{ $t("product.costs.calculateCostBreakdown") }}
      </button>
      <div
        v-else-if="bidCostsStatus === 'pending'"
        class="flex items-center justify-center h-11"
      >
        <BaseIcon
          name="spinner"
          class="mr-2 h-4 flex justify-center animate-spin"
        />
        <span class="uppercase text-sm text-platform-text-1">
          {{ $t("product.costs.calculating") }}
        </span>
      </div>
      <div
        v-else-if="bidCosts?.totalPrice.currency"
        class="product-bid-costs__container"
      >
        <button
          class="flex items-start w-full justify-between h-11"
          type="button"
          @click="costBreakDownOpen = !costBreakDownOpen"
        >
          <div class="flex-1 text-left">
            <div
              class="product-bid-costs__toggle font-semibold"
              data-test="product-bid-costs-toggle"
            >
              <p>{{ $t("product.costs.subtotal") }}</p>
              <p class="product-bid-costs__toggle-text">
                <BaseCurrency
                  :show-decimals="true"
                  :value="bidCosts.totalPrice.price"
                  :iso="bidCosts.totalPrice.currency"
                  currency-size="xs"
                />
              </p>
            </div>
            <p
              v-if="totalCostsInfo"
              class="text-platform-text-3 text-xs mt-1"
              data-test="product-bid-costs-info-message"
            >
              {{ $t(totalCostsInfo) }}
            </p>
          </div>

          <BaseAnimatedChevronIcon
            class="product-bid-costs__toggle-icon"
            :direction="costBreakDownOpen ? 'up' : 'down'"
          />
        </button>

        <ProductCosts
          v-if="costBreakDownOpen"
          data-test="product-bid-costs-info"
          class="pt-2"
          :bid="{
            costs: bidCosts.bid,
            productName: product.name || '-',
            eventName: eventName,
          }"
          :buyers-premium="{
            costs: bidCosts.buyersPremium,
            percentage: bidCosts.buyersPremiumPercentage,
          }"
          :vat="{
            costs: bidCosts.vat,
            percentage: bidCosts.vatPercentage,
            itemVatExplanation: bidCosts.itemVatExplanation,
            handlingVatExplanation: bidCosts.handlingVatExplanation,
            vatNumberCheckStatus: bidCosts.vatNumberCheckStatus,
          }"
          :shipping="{
            costs: bidCosts.estimatedShippingCost,
            officeName: bidCosts.shippingOfficeName,
            officeCountryCode: bidCosts.shippingOfficeCountryCode,
          }"
          :total="{ costs: bidCosts.totalPrice }"
          :options="{
            priceVariant: 'estimated',
            isShippingCountryProvided: Boolean(
              providedFilters.destinationCountryCode,
            ),
          }"
        />
      </div>
      <div v-if="bidCostsStatus === 'error'">
        <I18nT
          scope="global"
          keypath="product.costs.error"
          class="product-bid-costs__error"
          tag="div"
        >
          <template #PLACEHOLDER_contactSupport>
            <BaseCustomerSupportLink>
              <strong>{{ $t("common.contactSupport") }}</strong>
            </BaseCustomerSupportLink>
          </template>
        </I18nT>
      </div>
    </BaseTransitionFadeBetween>

    <template #fallback>
      <BaseSkeletonLoader
        :height="44"
        width="100%"
      />
    </template>
  </ClientOnly>
</template>

<style lang="postcss" scoped>
.product-bid-costs {
  &__error {
    @apply items-center justify-center;

    a {
      @apply font-light underline;
    }
  }
  &__toggle {
    @apply flex items-center justify-between;
    @apply w-full gap-1;

    &-text {
      @apply flex items-center justify-center;
    }
  }
}
</style>
